/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { canonicalJson } from "./wallet-link-crypto";

const DATABASE_NAME = "synergy-horizon-wallet-link";
const DATABASE_VERSION = 1;
const SESSION_STORE = "sessions";
const AUTHORIZATION_STORE = "authorizations";
const ACTIVE_SESSION_KEY = "horizon";
const PENDING_AUTHORIZATION_KEY = "horizon";

function toArrayBuffer(value) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function indexedDb() {
  if (!globalThis.indexedDB) throw new Error("Secure Wallet Link session storage is unavailable in this browser.");
  return globalThis.indexedDB;
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDb().open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(SESSION_STORE)) request.result.createObjectStore(SESSION_STORE);
      if (!request.result.objectStoreNames.contains(AUTHORIZATION_STORE)) request.result.createObjectStore(AUTHORIZATION_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Unable to open secure Wallet Link storage."));
    request.onblocked = () => reject(new Error("Secure Wallet Link storage is blocked by another Horizon tab."));
  });
}

async function transact(storeName, mode, operation) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const request = operation(transaction.objectStore(storeName));
    let result;
    request.onsuccess = () => { result = request.result; };
    request.onerror = () => reject(request.error || new Error("Secure Wallet Link storage request failed."));
    transaction.onabort = () => reject(transaction.error || new Error("Secure Wallet Link storage transaction was aborted."));
    transaction.oncomplete = () => {
      database.close();
      resolve(result);
    };
  });
}

export async function saveWalletLinkSession(record) {
  await transact(SESSION_STORE, "readwrite", (store) => store.put(record, ACTIVE_SESSION_KEY));
}

export async function loadWalletLinkSession() {
  const value = await transact(SESSION_STORE, "readonly", (store) => store.get(ACTIVE_SESSION_KEY));
  if (!value || value.recordVersion !== 1 || value.protocolVersion !== 2 || value.dappId !== "horizon") return null;
  return value;
}

export async function removeWalletLinkSession() {
  await transact(SESSION_STORE, "readwrite", (store) => store.delete(ACTIVE_SESSION_KEY));
}

export function storedWalletLinkParticipant(record) {
  return {
    signingPublicKey: record.signingPublicKey,
    signingPrivateKey: record.signingPrivateKey,
    transportPublicKey: record.transportPublicKey,
    transportPrivateKey: record.transportPrivateKey
  };
}

function authorizationAad(record) {
  return new TextEncoder().encode(canonicalJson({
    type: "synergy-horizon-wallet-link-pending-authorization",
    version: 1,
    authorizationRequestId: record.authorizationRequestId,
    expiresAt: record.expiresAt,
    proposalBodyText: record.proposalBodyText
  }));
}

export async function protectWalletLinkAuthorization(input) {
  const verifierWrappingKey = await globalThis.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
  if (verifierWrappingKey.extractable) throw new Error("Wallet Link refused an extractable verifier wrapping key.");
  const verifierNonce = new Uint8Array(12);
  globalThis.crypto.getRandomValues(verifierNonce);
  const base = {
    recordVersion: 1,
    authorizationRequestId: input.authorizationRequestId,
    hubAuthorizeUrl: input.hubAuthorizeUrl,
    expiresAt: input.expiresAt,
    proposal: input.proposal,
    proposalBodyText: input.proposalBodyText,
    signingPublicKey: input.participant.signingPublicKey,
    signingPrivateKey: input.participant.signingPrivateKey,
    transportPublicKey: input.participant.transportPublicKey,
    transportPrivateKey: input.participant.transportPrivateKey
  };
  const verifierCiphertext = await globalThis.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: toArrayBuffer(verifierNonce),
      additionalData: toArrayBuffer(authorizationAad(base)),
      tagLength: 128
    },
    verifierWrappingKey,
    toArrayBuffer(new TextEncoder().encode(input.codeVerifier))
  );
  return { ...base, verifierWrappingKey, verifierNonce, verifierCiphertext };
}

export async function saveWalletLinkAuthorization(input) {
  const record = await protectWalletLinkAuthorization(input);
  await transact(AUTHORIZATION_STORE, "readwrite", (store) => store.put(record, PENDING_AUTHORIZATION_KEY));
}

export async function loadWalletLinkAuthorization() {
  const record = await transact(AUTHORIZATION_STORE, "readonly", (store) => store.get(PENDING_AUTHORIZATION_KEY));
  return record?.recordVersion === 1 ? record : null;
}

export async function decryptWalletLinkAuthorizationVerifier(record) {
  if (record.verifierWrappingKey.extractable || record.verifierWrappingKey.algorithm?.name !== "AES-GCM") {
    throw new Error("Wallet Link pending authorization wrapping key is invalid.");
  }
  let plaintext;
  try {
    plaintext = await globalThis.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: toArrayBuffer(record.verifierNonce),
        additionalData: toArrayBuffer(authorizationAad(record)),
        tagLength: 128
      },
      record.verifierWrappingKey,
      record.verifierCiphertext
    );
  } catch {
    throw new Error("Wallet Link pending authorization could not be authenticated.");
  }
  const verifier = new TextDecoder().decode(plaintext);
  if (!/^[A-Za-z0-9_-]{43,128}$/.test(verifier)) throw new Error("Wallet Link pending PKCE verifier is invalid.");
  return verifier;
}

export async function removeWalletLinkAuthorization() {
  await transact(AUTHORIZATION_STORE, "readwrite", (store) => store.delete(PENDING_AUTHORIZATION_KEY));
}

export function storedWalletLinkAuthorizationParticipant(record) {
  return {
    signingPublicKey: record.signingPublicKey,
    signingPrivateKey: record.signingPrivateKey,
    transportPublicKey: record.transportPublicKey,
    transportPrivateKey: record.transportPrivateKey
  };
}
